import { useRef, type ReactNode } from "react"
import { useBenefitToolView } from "@/features/benefit-workspace/useBenefitToolView"
import { IconShieldCheck, IconUsers } from "@tabler/icons-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { MountedBenefitTools } from "./mounted-benefit-tools"
import { useHasOpenBenefitEditors } from "./benefit-edit-scope"
import { demographicsSummary } from "@/lib/demographics-summary"
import type { BenefitDetailDto } from "@/lib/benefit-types"
import "./demographics-details.css"

export function DemographicsDetails({ benefit, children, correction }: {
  benefit: BenefitDetailDto; children: ReactNode; correction: ReactNode
}) {
  const tools = useBenefitToolView()
  const open = tools.view?.section === "demographics"
  const trigger = useRef<HTMLButtonElement>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const editing = useHasOpenBenefitEditors()
  const summary = demographicsSummary(benefit)
  return <section className="demographics-workspace" aria-labelledby="demographics-heading">
    <header>
      <h2 id="demographics-heading" ref={heading} tabIndex={-1}>Demographics</h2>
      <div className="demographics-summary" aria-label="Household summary">
        <span><IconUsers className="size-4" /><strong>{summary.members}</strong> {summary.members === 1 ? "member" : "members"}</span>
        <span>Recorded age counts: <strong>{summary.adults ?? "Unavailable"}</strong> {summary.adults === 1 ? "adult" : "adults"} · <strong>{summary.children ?? "Unavailable"}</strong> {summary.children === 1 ? "child" : "children"}</span>
        <span>Applicant <strong>{summary.applicant}</strong></span>
      </div>
    </header>
    {children}
    <aside className="demographics-correction">
      <IconShieldCheck className="size-5 shrink-0" />
      <div><h3>Applicant &amp; household correction</h3><p>Changing the applicant requires a separate correction.</p></div>
      <Button ref={trigger} variant="outline" size="sm" onClick={() => {
        if (editing) { toast.error("Save or cancel the open benefit editor before opening another panel."); return }
        tools.select({ section: "demographics", tool: "correction" })
      }}>View correction scope</Button>
    </aside>
    <MountedBenefitTools scope="demographics" open={open} onClose={() => tools.close("demographics")} onReturnFocus={() => (trigger.current?.isConnected ? trigger.current : heading.current)?.focus()}
      title="Applicant & household correction" description="Review the scope before opening the existing correction preview. Nothing changes until you confirm it.">
      <div className="demographics-correction-scope">
        <h3>Application membership, not shared identity</h3>
        <p>The correction keeps this file and its history. Removing a member also removes their income from this application, but does not delete their person record.</p>
        <p>Retained members keep their income and demographics. Relationships and affected intake facts must be reviewed and confirmed in the correction preview.</p>
        <p>Member editors label shared identity fields separately. Those identity changes affect the person across applications; ordinary demographic answers stay on this application.</p>
        {correction}
      </div>
    </MountedBenefitTools>
  </section>
}
